import type { ApiClient } from '@acies/platform';
import { Effect } from 'effect';
import type { SearchResult } from '../domain/search-results';
import { runCall } from './runtime';
import { type SearchError, type SearchOptions, searchAt, searchCached, searchUrl } from './search';
import type { EnginePath } from './shared';

/** The resource segment the store's common texts live under, which is what a text position is a position of. */
const resource = 'common-texts';

/**
 * What a common text search asks for, which is what any search asks for.
 *
 * `name` is the API's word for the text searched with, whichever collection it is put to. This one matches it against the text's code — the key, `A01` — so the field the user types into is the key field, as it is for the other two kinds.
 */
export type SearchCommonTextsOptions = SearchOptions;

/** Everything searching the store for common texts can fail with. */
export type SearchCommonTextsError = SearchError;

/** The calls against the store's common texts. */
export interface CommonTextCalls {
	/**
	 * Searches the store's common texts by code.
	 *
	 * Answers with at most `limit` texts, each as its code and its heading — the `name`, which is what a text position is described by. The lines a text runs to are in the answer as well, and are not decoded: nothing reads them yet.
	 */
	readonly searchCommonTexts: (options: SearchCommonTextsOptions) => Effect.Effect<ReadonlyArray<SearchResult>, SearchCommonTextsError>;
}

/**
 * Implements the common text calls over the client and store path every call shares.
 *
 * `typeof ApiClient.Service` is the shape `ApiClient` provides — the client with this API's host, bearer token, account header, auth failures and retry policy already applied.
 */
export function makeCommonTextCalls(client: typeof ApiClient.Service, storePath: EnginePath): CommonTextCalls {
	return {
		searchCommonTexts: (options) => storePath(resource).pipe(Effect.flatMap((path) => searchAt(client, searchUrl(path, options)))),
	};
}

/**
 * Searches the store's common texts from outside Effect.
 *
 * The awaitable form of {@link CommonTextCalls.searchCommonTexts}, for the key field of a text position, which is a web component rather than an Effect program. The store is the account's default engine, read per call, as it is for `searchInventoryItems`.
 */
export const searchCommonTexts = (limit: number, name: string): Promise<ReadonlyArray<SearchResult> | SearchCommonTextsError> => runCall(({ storePath }) => storePath(resource).pipe(Effect.flatMap((path) => searchCached(path, { limit, name }))));
